import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Button,
  Space,
  Spin,
  Alert,
  Card,
  Row,
  Col,
  Table,
  Modal,
  InputNumber,
  Divider,
  Tabs,
  TabsProps,
  Select,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import * as Types from '@/types'
import { RootState, AppDispatch } from '@/renderer/store'
import { fetchUnits, fetchPanels, fetchPressureEquipment, fetchConsumables } from '@/renderer/store/equipmentSlice'

interface DailyReportFooterProps {
  footerData?: Types.FooterData
  onUpdate: (data: Types.FooterDataUpdateRequest) => Promise<void>
  loading?: boolean
}

interface UnitEntry {
  key?: string
  unit_id: string  // Reference to Unit.id
  hours?: number
}

interface PanelEntry {
  key?: string
  panel_id: string  // Reference to Panel.id
  hours?: number
}

interface PressureEquipmentEntry {
  key?: string
  equipment_id: string  // Reference to PressureEquipment.id
  hours?: number
}

interface ConsumableEntry {
  key?: string
  consumable_id: string  // Reference to Consumable.id
  quantity: number
}

export const DailyReportFooter: React.FC<DailyReportFooterProps> = ({
  footerData,
  onUpdate,
  loading = false,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [form] = Form.useForm()
  const [unitsForm] = Form.useForm()
  const [panelsForm] = Form.useForm()
  const [pressureEquipmentForm] = Form.useForm()
  const [consumablesForm] = Form.useForm()

  // Equipment state from Redux
  const units = useSelector((state: RootState) => state.equipment.units.items)
  const panels = useSelector((state: RootState) => state.equipment.panels.items)
  const pressureEquipment = useSelector((state: RootState) => state.equipment.pressureEquipment.items)
  const consumables = useSelector((state: RootState) => state.equipment.consumables.items)
  const equipmentLoading = useSelector((state: RootState) =>
    state.equipment.units.isLoading ||
    state.equipment.panels.isLoading ||
    state.equipment.pressureEquipment.isLoading ||
    state.equipment.consumables.isLoading
  )

  // Local state for selected equipment
  const [error, setError] = useState<string | null>(null)
  const [selectedUnits, setSelectedUnits] = useState<UnitEntry[]>(footerData?.units_in_use || [])
  const [selectedPanels, setSelectedPanels] = useState<PanelEntry[]>(footerData?.panels_in_use || [])
  const [selectedEquipment, setSelectedEquipment] = useState<PressureEquipmentEntry[]>(footerData?.pressure_equipment_used || [])
  const [cableData, setCableData] = useState(footerData?.cable_usage || {})
  const [crewTravel, setCrewTravel] = useState(footerData?.crew_travel || {})
  const [selectedConsumables, setSelectedConsumables] = useState<ConsumableEntry[]>(footerData?.consumables_used || [])

  // Load equipment on component mount
  useEffect(() => {
    dispatch(fetchUnits({ skip: 0, limit: 100 }))
    dispatch(fetchPanels({ skip: 0, limit: 100 }))
    dispatch(fetchPressureEquipment({ skip: 0, limit: 100 }))
    dispatch(fetchConsumables({ skip: 0, limit: 100 }))
  }, [dispatch])

  const [isUnitsModalVisible, setIsUnitsModalVisible] = useState(false)
  const [editingUnit, setEditingUnit] = useState<UnitEntry | null>(null)
  const [isPanelsModalVisible, setIsPanelsModalVisible] = useState(false)
  const [editingPanel, setEditingPanel] = useState<PanelEntry | null>(null)
  const [isPressureEquipmentModalVisible, setIsPressureEquipmentModalVisible] = useState(false)
  const [editingEquipment, setEditingEquipment] = useState<PressureEquipmentEntry | null>(null)
  const [isConsumablesModalVisible, setIsConsumablesModalVisible] = useState(false)
  const [editingConsumable, setEditingConsumable] = useState<ConsumableEntry | null>(null)

  const handleSaveFooter = async () => {
    try {
      setError(null)
      const payload: Types.FooterDataUpdateRequest = {
        units_in_use: selectedUnits,
        panels_in_use: selectedPanels,
        pressure_equipment_used: selectedEquipment,
        cable_usage: cableData,
        crew_travel: crewTravel,
        consumables_used: selectedConsumables,
      }
      await onUpdate(payload)
    } catch (err: any) {
      setError(err.message || 'Failed to update footer')
    }
  }

  // Units handlers
  const handleAddUnit = () => {
    setEditingUnit(null)
    unitsForm.resetFields()
    setIsUnitsModalVisible(true)
  }

  const handleEditUnit = (record: UnitEntry) => {
    setEditingUnit(record)
    unitsForm.setFieldsValue(record)
    setIsUnitsModalVisible(true)
  }

  const handleDeleteUnit = (key: string | undefined) => {
    const newUnits = selectedUnits.filter((u) => u.key !== key)
    setSelectedUnits(newUnits)
  }

  const handleUnitsModalOk = async () => {
    try {
      const values = await unitsForm.validateFields()
      if (editingUnit) {
        const newUnits = selectedUnits.map((u) => (u.key === editingUnit.key ? { ...u, ...values } : u))
        setSelectedUnits(newUnits)
      } else {
        setSelectedUnits([...selectedUnits, { key: `unit-${Date.now()}`, ...values }])
      }
      setIsUnitsModalVisible(false)
      unitsForm.resetFields()
      setEditingUnit(null)
    } catch (err) {
      // Validation failed
    }
  }

  // Panels handlers
  const handleAddPanel = () => {
    setEditingPanel(null)
    panelsForm.resetFields()
    setIsPanelsModalVisible(true)
  }

  const handleEditPanel = (record: PanelEntry) => {
    setEditingPanel(record)
    panelsForm.setFieldsValue(record)
    setIsPanelsModalVisible(true)
  }

  const handleDeletePanel = (key: string | undefined) => {
    const newPanels = selectedPanels.filter((p) => p.key !== key)
    setSelectedPanels(newPanels)
  }

  const handlePanelsModalOk = async () => {
    try {
      const values = await panelsForm.validateFields()
      if (editingPanel) {
        const newPanels = selectedPanels.map((p) => (p.key === editingPanel.key ? { ...p, ...values } : p))
        setSelectedPanels(newPanels)
      } else {
        setSelectedPanels([...selectedPanels, { key: `panel-${Date.now()}`, ...values }])
      }
      setIsPanelsModalVisible(false)
      panelsForm.resetFields()
      setEditingPanel(null)
    } catch (err) {
      // Validation failed
    }
  }

  // Pressure Equipment handlers
  const handleAddPressureEquipment = () => {
    setEditingEquipment(null)
    pressureEquipmentForm.resetFields()
    setIsPressureEquipmentModalVisible(true)
  }

  const handleEditPressureEquipment = (record: PressureEquipmentEntry) => {
    setEditingEquipment(record)
    pressureEquipmentForm.setFieldsValue(record)
    setIsPressureEquipmentModalVisible(true)
  }

  const handleDeletePressureEquipment = (key: string | undefined) => {
    const newEquipment = selectedEquipment.filter((e) => e.key !== key)
    setSelectedEquipment(newEquipment)
  }

  const handlePressureEquipmentModalOk = async () => {
    try {
      const values = await pressureEquipmentForm.validateFields()
      if (editingEquipment) {
        const newEquipment = selectedEquipment.map((e) => (e.key === editingEquipment.key ? { ...e, ...values } : e))
        setSelectedEquipment(newEquipment)
      } else {
        setSelectedEquipment([...selectedEquipment, { key: `equipment-${Date.now()}`, ...values }])
      }
      setIsPressureEquipmentModalVisible(false)
      pressureEquipmentForm.resetFields()
      setEditingEquipment(null)
    } catch (err) {
      // Validation failed
    }
  }

  // Consumables handlers
  const handleAddConsumable = () => {
    setEditingConsumable(null)
    consumablesForm.resetFields()
    setIsConsumablesModalVisible(true)
  }

  const handleEditConsumable = (record: ConsumableEntry) => {
    setEditingConsumable(record)
    consumablesForm.setFieldsValue(record)
    setIsConsumablesModalVisible(true)
  }

  const handleDeleteConsumable = (key: string | undefined) => {
    const newConsumables = selectedConsumables.filter((c) => c.key !== key)
    setSelectedConsumables(newConsumables)
  }

  const handleConsumablesModalOk = async () => {
    try {
      const values = await consumablesForm.validateFields()
      if (editingConsumable) {
        const newConsumables = selectedConsumables.map((c) =>
          c.key === editingConsumable.key ? { ...c, ...values } : c
        )
        setSelectedConsumables(newConsumables)
      } else {
        setSelectedConsumables([...selectedConsumables, { key: `consumable-${Date.now()}`, ...values }])
      }
      setIsConsumablesModalVisible(false)
      consumablesForm.resetFields()
      setEditingConsumable(null)
    } catch (err) {
      // Validation failed
    }
  }

  const unitsColumns = [
    {
      title: 'Unit',
      dataIndex: 'unit_id',
      key: 'unit_id',
      render: (unitId: string) => {
        const unit = units.find((u) => u.id === unitId)
        return unit ? `${unit.unit_id} (${unit.unit_type})` : unitId
      },
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (hours: number | undefined) => (hours ? `${hours.toFixed(1)} hrs` : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: UnitEntry) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditUnit(record)} />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteUnit(record.key)} />
        </Space>
      ),
    },
  ]

  const panelsColumns = [
    {
      title: 'Panel',
      dataIndex: 'panel_id',
      key: 'panel_id',
      render: (panelId: string) => {
        const panel = panels.find((p) => p.id === panelId)
        return panel ? `${panel.key_or_sn} (${panel.panel_type})` : panelId
      },
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (hours: number | undefined) => (hours ? `${hours.toFixed(1)} hrs` : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: PanelEntry) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditPanel(record)} />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeletePanel(record.key)} />
        </Space>
      ),
    },
  ]

  const pressureEquipmentColumns = [
    {
      title: 'Equipment',
      dataIndex: 'equipment_id',
      key: 'equipment_id',
      render: (equipmentId: string) => {
        const equip = pressureEquipment.find((e) => e.id === equipmentId)
        return equip ? `${equip.serial_number} (${equip.equipment_type})` : equipmentId
      },
    },
    {
      title: 'Max Pressure',
      dataIndex: 'max_pressure',
      key: 'max_pressure',
      render: (max_pressure: number, record: any) => {
        const equip = pressureEquipment.find((e) => e.id === record.equipment_id)
        return equip ? `${equip.max_pressure} ${equip.pressure_unit}` : '-'
      },
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (hours: number | undefined) => (hours ? `${hours.toFixed(1)} hrs` : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: PressureEquipmentEntry) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditPressureEquipment(record)} />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeletePressureEquipment(record.key)}
          />
        </Space>
      ),
    },
  ]

  const consumablesColumns = [
    {
      title: 'Item',
      dataIndex: 'consumable_id',
      key: 'consumable_id',
      render: (consumableId: string) => {
        const consumable = consumables.find((c) => c.id === consumableId)
        return consumable ? consumable.item_name : consumableId
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Unit of Measure',
      dataIndex: 'unit_of_measure',
      key: 'unit_of_measure',
      render: (_: any, record: ConsumableEntry) => {
        const consumable = consumables.find((c) => c.id === record.consumable_id)
        return consumable ? consumable.unit_of_measure : '-'
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ConsumableEntry) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditConsumable(record)} />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteConsumable(record.key)}
          />
        </Space>
      ),
    },
  ]

  const tabItems: TabsProps['items'] = [
    {
      key: '1',
      label: 'Units in Use',
      children: (
        <Spin spinning={equipmentLoading}>
          <div>
            {selectedUnits.length > 0 ? (
              <Table
                dataSource={selectedUnits.map((u, idx) => ({ ...u, key: u.key || idx }))}
                columns={unitsColumns}
                pagination={false}
                bordered
                size="small"
                style={{ marginBottom: '16px' }}
              />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No units added yet.
              </div>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddUnit}>
              Add Unit
            </Button>
          </div>
        </Spin>
      ),
    },
    {
      key: '2',
      label: 'Electronic Panels',
      children: (
        <Spin spinning={equipmentLoading}>
          <div>
            {selectedPanels.length > 0 ? (
              <Table
                dataSource={selectedPanels.map((p, idx) => ({ ...p, key: p.key || idx }))}
                columns={panelsColumns}
                pagination={false}
                bordered
                size="small"
                style={{ marginBottom: '16px' }}
              />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No panels added yet.
              </div>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPanel}>
              Add Panel
            </Button>
          </div>
        </Spin>
      ),
    },
    {
      key: '3',
      label: 'Pressure Equipment',
      children: (
        <Spin spinning={equipmentLoading}>
          <div>
            {selectedEquipment.length > 0 ? (
              <Table
                dataSource={selectedEquipment.map((e, idx) => ({ ...e, key: e.key || idx }))}
                columns={pressureEquipmentColumns}
                pagination={false}
                bordered
                size="small"
                style={{ marginBottom: '16px' }}
              />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No pressure equipment added yet.
              </div>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPressureEquipment}>
              Add Pressure Equipment
            </Button>
          </div>
        </Spin>
      ),
    },
    {
      key: '4',
      label: 'Cable Usage',
      children: (
        <Form layout="vertical">
          <Form.Item label="Cable ID/Serial">
            <Input
              placeholder="e.g., WL-Cable-123"
              value={cableData.cable_id || ''}
              onChange={(e) => setCableData({ ...cableData, cable_id: e.target.value })}
            />
          </Form.Item>
          <Form.Item label="Total Meters">
            <InputNumber
              min={0}
              step={10}
              placeholder="0"
              value={cableData.total_meters || undefined}
              onChange={(v) => setCableData({ ...cableData, total_meters: v || undefined })}
            />
          </Form.Item>
          <Form.Item label="Number of Runs">
            <InputNumber
              min={0}
              placeholder="0"
              value={cableData.runs || undefined}
              onChange={(v) => setCableData({ ...cableData, runs: v || undefined })}
            />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: '5',
      label: 'Crew Travel',
      children: (
        <Form layout="vertical">
          <Form.Item label="Vehicle">
            <Input
              placeholder="e.g., Pickup Truck F-150"
              value={crewTravel.vehicle || ''}
              onChange={(e) => setCrewTravel({ ...crewTravel, vehicle: e.target.value })}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Odometer Start (km)">
                <InputNumber
                  min={0}
                  placeholder="0"
                  value={crewTravel.odometer_start || undefined}
                  onChange={(v) => setCrewTravel({ ...crewTravel, odometer_start: v || undefined })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Odometer End (km)">
                <InputNumber
                  min={0}
                  placeholder="0"
                  value={crewTravel.odometer_end || undefined}
                  onChange={(v) => setCrewTravel({ ...crewTravel, odometer_end: v || undefined })}
                />
              </Form.Item>
            </Col>
          </Row>
          {crewTravel.odometer_start && crewTravel.odometer_end && (
            <div style={{ padding: '8px 12px', backgroundColor: '#e6f7ff', borderRadius: '4px' }}>
              <strong>Total Distance: {crewTravel.odometer_end - crewTravel.odometer_start} km</strong>
            </div>
          )}
        </Form>
      ),
    },
    {
      key: '6',
      label: 'Consumables',
      children: (
        <Spin spinning={equipmentLoading}>
          <div>
            {selectedConsumables.length > 0 ? (
              <Table
                dataSource={selectedConsumables.map((c, idx) => ({ ...c, key: c.key || idx }))}
                columns={consumablesColumns}
                pagination={false}
                bordered
                size="small"
                style={{ marginBottom: '16px' }}
              />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No consumables added yet.
              </div>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddConsumable}>
              Add Consumable
            </Button>
          </div>
        </Spin>
      ),
    },
  ]

  return (
    <div style={{ padding: '20px 0' }}>
      {error && <Alert message={error} type="error" style={{ marginBottom: '16px' }} closable />}

      <Spin spinning={loading}>
        <Card title="Daily Report Footer" size="small">
          <Tabs items={tabItems} />

          <Divider />

          <Button type="primary" size="large" onClick={handleSaveFooter} loading={loading}>
            Save All Footer Data
          </Button>
        </Card>

        {/* Units Modal */}
        <Modal
          title={editingUnit ? 'Edit Unit' : 'Add Unit'}
          open={isUnitsModalVisible}
          onOk={handleUnitsModalOk}
          onCancel={() => setIsUnitsModalVisible(false)}
        >
          <Form form={unitsForm} layout="vertical">
            <Form.Item
              name="unit_id"
              label="Unit"
              rules={[{ required: true, message: 'Please select a unit' }]}
            >
              <Select
                placeholder="Select unit from inventory"
                loading={equipmentLoading}
                options={units.map((u) => ({
                  value: u.id,
                  label: `${u.unit_id} (${u.unit_type})`,
                }))}
              />
            </Form.Item>
            <Form.Item name="hours" label="Hours Used (Optional)">
              <InputNumber min={0} max={24} step={0.5} placeholder="0" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Panels Modal */}
        <Modal
          title={editingPanel ? 'Edit Panel' : 'Add Panel'}
          open={isPanelsModalVisible}
          onOk={handlePanelsModalOk}
          onCancel={() => setIsPanelsModalVisible(false)}
        >
          <Form form={panelsForm} layout="vertical">
            <Form.Item
              name="panel_id"
              label="Panel"
              rules={[{ required: true, message: 'Please select a panel' }]}
            >
              <Select
                placeholder="Select panel from inventory"
                loading={equipmentLoading}
                options={panels.map((p) => ({
                  value: p.id,
                  label: `${p.key_or_sn} (${p.panel_type})`,
                }))}
              />
            </Form.Item>
            <Form.Item name="hours" label="Hours Used (Optional)">
              <InputNumber min={0} max={24} step={0.5} placeholder="0" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Pressure Equipment Modal */}
        <Modal
          title={editingEquipment ? 'Edit Pressure Equipment' : 'Add Pressure Equipment'}
          open={isPressureEquipmentModalVisible}
          onOk={handlePressureEquipmentModalOk}
          onCancel={() => setIsPressureEquipmentModalVisible(false)}
        >
          <Form form={pressureEquipmentForm} layout="vertical">
            <Form.Item
              name="equipment_id"
              label="Pressure Equipment"
              rules={[{ required: true, message: 'Please select equipment' }]}
            >
              <Select
                placeholder="Select pressure equipment from inventory"
                loading={equipmentLoading}
                options={pressureEquipment.map((e) => ({
                  value: e.id,
                  label: `${e.serial_number} (${e.equipment_type}) - ${e.max_pressure}${e.pressure_unit}`,
                }))}
              />
            </Form.Item>
            <Form.Item name="hours" label="Hours Used (Optional)">
              <InputNumber min={0} max={24} step={0.5} placeholder="0" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Consumables Modal */}
        <Modal
          title={editingConsumable ? 'Edit Consumable' : 'Add Consumable'}
          open={isConsumablesModalVisible}
          onOk={handleConsumablesModalOk}
          onCancel={() => setIsConsumablesModalVisible(false)}
        >
          <Form form={consumablesForm} layout="vertical">
            <Form.Item
              name="consumable_id"
              label="Consumable"
              rules={[{ required: true, message: 'Please select a consumable' }]}
            >
              <Select
                placeholder="Select consumable from inventory"
                loading={equipmentLoading}
                options={consumables.map((c) => ({
                  value: c.id,
                  label: `${c.item_name} (${c.unit_of_measure})`,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="quantity"
              label="Quantity Used"
              rules={[{ required: true, message: 'Please enter quantity' }]}
            >
              <InputNumber min={0} placeholder="0" />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </div>
  )
}

export default DailyReportFooter
